/**
 * Cadence verdict badge tokens — the palette, in one place, as SOLID colours.
 *
 * WHY SOLID: the first version of these badges used Tailwind alpha utilities
 * (`bg-red-500/25 text-red-200`). On this app's light theme — hg-surface is #FFFFFF and
 * hg-bg is #FAFAF7 — that composited to pale-pink text on pale-pink chip: a measured
 * 1.11:1 for MISSED and 1.12:1 for THIN, at 9px, against a WCAG AA floor of 4.5:1. The
 * badge is the element that states how bad the problem is, and it was the least legible
 * thing on the card.
 *
 * Alpha was the root cause, not just the values: a translucent chip's effective colour
 * depends on whatever it happens to be sitting on, so it cannot be checked without
 * compositing, and it silently changes the moment a parent background changes. These are
 * opaque hex pairs applied as inline styles, which makes the contrast exact, checkable by
 * arithmetic, and stable regardless of what the card is layered over.
 *
 * EVERY pairing here is asserted at >= 4.5:1 by cadenceBadges.contrast.test.js. Adding a
 * verdict without a readable pairing breaks the build — which is the point: the 56 tests
 * that passed while these pixels were unreadable all asserted class names, and jsdom
 * neither composites alpha nor computes contrast, so none of them could ever have caught
 * this.
 */

// Card surfaces. Opaque, so the text tokens below have one known background each.
export const CARD_BG = {
  MISSED: '#FEF2F2',
  GAP: '#FFF7ED',
  THIN: '#FFFBEB',
  HELD: '#FFFFFF',
  PAUSED: '#FFFFFF',
  OK: '#FFFFFF',
};

export const CARD_BORDER = {
  MISSED: '#DC2626',
  GAP: '#EA580C',
  THIN: '#D97706',
  // hg-border, the app-wide card edge. See NEUTRAL_BORDER_VERDICTS below for why these
  // are held to a different standard than the three that carry a signal.
  HELD: '#E2E2DC',
  PAUSED: '#E2E2DC',
  OK: '#E2E2DC',
};

// The three borders that COMMUNICATE severity, and so are bound by the 3:1 floor for
// non-text UI. The neutral three are decorative card edges reusing the existing hg-border
// token (#E2E2DC on #FAFAF7 is 1.24:1) — changing that would restyle every card in the
// Hub, which is a design decision well outside a contrast fix. Listed explicitly so the
// exemption is visible in code review rather than achieved by not writing the test.
export const SIGNAL_BORDER_VERDICTS = ['MISSED', 'GAP', 'THIN'];
export const NEUTRAL_BORDER_VERDICTS = ['HELD', 'PAUSED', 'OK'];

// Badge chip: dark text on a light tint of the same hue.
//
// The chip tints are deliberately a step darker than the card tint they sit on. The first
// attempt paired a -100 chip with a -50 card of the same hue: the TEXT passed AA, but the
// chip was 1.08-1.12:1 against the card, i.e. a perfectly legible label floating on no
// visible chip at all. Legible text in an invisible container is still a broken badge, so
// both relationships are asserted.
//
// MISSED stays at red-200 rather than red-300 because #991B1B on red-300 measures 4.38:1
// — under the floor. The darker chip looked more urgent and would have failed; the test
// is what caught that, not the eye.
export const BADGE = {
  MISSED: { fg: '#991B1B', bg: '#FECACA', icon: '#991B1B' },
  GAP: { fg: '#9A3412', bg: '#FED7AA', icon: '#9A3412' },
  THIN: { fg: '#92400E', bg: '#FCD34D', icon: '#92400E' },
  HELD: { fg: '#374151', bg: '#E5E7EB', icon: '#374151' },
  PAUSED: { fg: '#4B5563', bg: '#E5E7EB', icon: '#4B5563' },
  OK: { fg: '#14532D', bg: '#86EFAC', icon: '#14532D' },
};

// A chip that is legible but indistinguishable from its card is not a chip. 1.2:1 is a
// low bar on purpose — it catches "effectively the same colour", not mediocre styling.
export const CHIP_MIN_RATIO = 1.2;

// Body text that sits directly on a CARD_BG rather than on a chip. Checked against every
// card surface a hold row can appear on, not just the neutral one — a held slot is listed
// inside a MISSED card whenever both are true at once.
export const ON_CARD = {
  hold: '#374151',       // an active hold: neutral, deliberately not an alarm
  holdMeta: '#5C5C5C',   // hg-text-dim; the "lapses <date>" tail
  lapsed: '#9A3412',     // a hold that ran out: reads as attention again
};

// Below the AA floor the badge stops doing its job, so this is a build-breaking threshold,
// not a lint preference.
export const AA_MIN_RATIO = 4.5;

// 11px minimum. The old 9px compounded the contrast failure: small text needs MORE
// contrast to stay legible, not less, and 9px is under the 12px floor at which any of the
// usual "large text" relaxations would even apply.
export const BADGE_FONT_PX = 11;

export const VERDICTS = ['MISSED', 'GAP', 'THIN', 'HELD', 'PAUSED', 'OK'];

// --- WCAG 2.1 relative luminance + contrast ratio -----------------------------------
// Exported so the test computes ratios with the same arithmetic the palette was chosen
// with, rather than re-implementing (and possibly re-deriving a different answer).

export function hexToRgb(hex) {
  const h = String(hex).replace('#', '').trim();
  if (!/^[0-9a-fA-F]{6}$/.test(h)) throw new Error(`not a 6-digit hex colour: ${hex}`);
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

export function relativeLuminance(hex) {
  const [r, g, b] = hexToRgb(hex).map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(fg, bg) {
  const a = relativeLuminance(fg);
  const b = relativeLuminance(bg);
  const [hi, lo] = a > b ? [a, b] : [b, a];
  return (hi + 0.05) / (lo + 0.05);
}

/** Verdict -> badge/card tokens, falling back to the neutral pairing for an unknown
 *  verdict. The fallback is HELD's neutral grey rather than a red: an unrecognised
 *  verdict is a code bug, and shouting about it in the UI would be a worse bug. */
export function tokensFor(verdict) {
  const v = VERDICTS.includes(verdict) ? verdict : 'HELD';
  return { badge: BADGE[v], cardBg: CARD_BG[v], cardBorder: CARD_BORDER[v], resolved: v };
}
